import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Gift, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { TurnstileWidget } from "@/components/TurnstileWidget";

const CATEGORIES = ["food", "fitness", "wellness", "services", "retail", "beauty",
                    "auto", "petcare", "home", "entertainment", "other"];

const TURNSTILE_SITE_KEY = (process.env.REACT_APP_TURNSTILE_SITE_KEY || "").trim();

/**
 * Public perk-partner signup — lets a local business apply for a partner
 * account. Application lands in admin's queue.
 */
export default function PartnerSignupPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    business_name: "", contact_name: "", email: "", password: "",
    property_id: "", category: "food", description: "", website: "", phone: "",
  });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [properties, setProperties] = useState([]);
  const [turnstileToken, setTurnstileToken] = useState("");

  useEffect(() => {
    // Public read of community list for signup
    (async () => {
      try {
        const { data } = await api.get("/perk-partners/public/properties");
        setProperties(data.items || []);
        if (data.items?.length && !form.property_id) {
          setForm((f) => ({ ...f, property_id: data.items[0].id }));
        }
      } catch (err) { if (typeof console !== 'undefined') console.debug('pages/partner/PartnerSignupPage.jsx: swallowed', err); }
    })();
    // form.property_id is the initial empty string at mount; we only ever
    // want this to run once so the dropdown preselects the first community.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (!form.business_name || !form.email || !form.password || !form.property_id) {
      return toast.error("Fill business name, email, password, and community");
    }
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      return toast.error("Complete the bot check before submitting");
    }
    setBusy(true);
    try {
      await api.post("/perk-partners/signup", { ...form, turnstile_token: turnstileToken });
      setSubmitted(true);
    } catch (e) { toast.error(apiErrorMessage(e) || "Signup failed"); }
    setBusy(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-zinc-950 to-orange-950/20 text-white flex items-center justify-center p-6">
        <Card className="p-8 border-white/10 max-w-lg text-center" data-testid="partner-signup-success">
          <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-400 mb-3" />
          <div className="text-xl font-display font-semibold mb-2">Application received</div>
          <div className="text-sm text-zinc-400 mb-4">
            Thanks, {form.contact_name || "there"} — we&apos;ve queued {form.business_name} for admin review.
            You&apos;ll get an email as soon as your account is approved.
          </div>
          <Button onClick={() => navigate("/login")} className="bg-orange-500 hover:bg-orange-600 rounded-full">
            Back to login <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-zinc-950 to-orange-950/20 text-white flex items-center justify-center p-6">
      <Card className="p-6 border-white/10 max-w-2xl w-full" data-testid="partner-signup-form">
        <div className="flex items-center gap-3 mb-4">
          <img src="/prestigious-logo.png" alt="Prestigious Property Management Services" className="h-12 w-12" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Perk Partner Portal</div>
            <div className="text-xl font-display font-semibold">Apply to publish perks</div>
          </div>
        </div>
        <p className="text-sm text-zinc-400 mb-4">
          Bring your business in front of verified residents in the community. Publish exclusive offers, track claim data, and see which perks land.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-zinc-400">Business name</Label>
            <Input value={form.business_name}
                   onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                   className="bg-white/[0.04] border-white/10" data-testid="partner-signup-business" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Contact name</Label>
            <Input value={form.contact_name}
                   onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                   className="bg-white/[0.04] border-white/10" data-testid="partner-signup-name" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Email</Label>
            <Input type="email" value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })}
                   className="bg-white/[0.04] border-white/10" data-testid="partner-signup-email" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Password (min 8 chars)</Label>
            <Input type="password" value={form.password}
                   onChange={(e) => setForm({ ...form, password: e.target.value })}
                   className="bg-white/[0.04] border-white/10" data-testid="partner-signup-password" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Community</Label>
            <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
              <SelectTrigger className="bg-white/[0.04] border-white/10" data-testid="partner-signup-community">
                <SelectValue placeholder="Choose the community" />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="bg-white/[0.04] border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs text-zinc-400">About your business</Label>
            <Textarea rows={3} value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className="bg-white/[0.04] border-white/10 text-sm" data-testid="partner-signup-desc" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Website</Label>
            <Input value={form.website}
                   onChange={(e) => setForm({ ...form, website: e.target.value })}
                   className="bg-white/[0.04] border-white/10" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Phone</Label>
            <Input value={form.phone}
                   onChange={(e) => setForm({ ...form, phone: e.target.value })}
                   className="bg-white/[0.04] border-white/10" />
          </div>
        </div>

        <div className="mt-4">
          <TurnstileWidget siteKey={TURNSTILE_SITE_KEY} onToken={setTurnstileToken} />
        </div>

        <Button onClick={save} disabled={busy || (!!TURNSTILE_SITE_KEY && !turnstileToken)}
                className="w-full mt-4 bg-orange-500 hover:bg-orange-600"
                data-testid="partner-signup-submit">
          {busy ? "Submitting…" : "Apply for partner account"}
        </Button>
      </Card>
    </div>
  );
}
